import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

import { useAppDispatch } from 'src/store/hooks';
import {
  useAuthorizeUserMutation,
  type AuthorizeUserRequest,
  type AuthorizedUser,
} from 'src/api/authApi';
import { setAuthorize } from 'src/storage/authorize';
import { setPittaRem } from 'src/utils/cookie';
import { getApiErrorCode } from 'src/utils/apiError';
import { setAuthStatus } from 'src/features/login/authSlice';
import { routePathConfig } from 'src/routes/config';
import { OAUTH_PARAMS } from 'src/constants/oauth';
import { API_ERROR_CODES } from 'src/constants/apiErrors';

interface UseLoginFlowOptions {
  csrfCode?: string;
  refreshCsrfCode?: () => Promise<string | null>;
}

interface UseLoginFlowResult {
  login: (credentials: AuthorizeUserRequest) => Promise<void>;
  isLoading: boolean;
  isError: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
}

const isExpiredCsrfError = (error: unknown): boolean => {
  return getApiErrorCode(error) === API_ERROR_CODES.EXPIRED_CSRF_CODE;
};

/**
 * Orchestrates the user-submitted login flow:
 *
 *   1. POST /authorize
 *   2. Persist `userPass` + token metadata to localStorage
 *   3. Optionally write the `pitta_rem` cookie (Remember Me)
 *   4. Update auth status in Redux
 *   5. Navigate to /grantConsent, preserving the current URL query string
 *
 * `login` rejects when the mutation fails so the caller can react (e.g. show
 * an error). Inspect `error` / `isError` for the latest mutation error.
 */
export const useLoginFlow = ({
  csrfCode,
  refreshCsrfCode,
}: UseLoginFlowOptions = {}): UseLoginFlowResult => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [authorizeUser, result] = useAuthorizeUserMutation();

  const login = useCallback(
    async (credentials: AuthorizeUserRequest) => {
      let activeCsrfCode = csrfCode;
      if (!activeCsrfCode && refreshCsrfCode) {
        activeCsrfCode = (await refreshCsrfCode()) ?? undefined;
      }
      if (!activeCsrfCode) {
        throw new Error('Missing CSRF Code');
      }

      let data: AuthorizedUser;
      try {
        data = await authorizeUser({
          ...credentials,
          csrfCode: activeCsrfCode,
        }).unwrap();
      } catch (error) {
        if (!isExpiredCsrfError(error) || !refreshCsrfCode) {
          throw error;
        }

        const refreshedCsrfCode = await refreshCsrfCode();
        if (!refreshedCsrfCode) {
          throw error;
        }

        data = await authorizeUser({
          ...credentials,
          csrfCode: refreshedCsrfCode,
        }).unwrap();
      }

      // The session itself arrived as an HttpOnly cookie on this response; only
      // non-secret display data is cached locally.
      const expiresAt = Math.floor(Date.now() / 1000) + data.expiresIn;
      setAuthorize({ account: data.user.account, expiresAt });
      if (credentials.rememberMe) {
        setPittaRem({ account: data.user.account, expiresAt });
      }
      dispatch(setAuthStatus('authenticated'));
      const hasClientId = Boolean(new URLSearchParams(location.search).get(OAUTH_PARAMS.CLIENT_ID));
      navigate({
        pathname: hasClientId ? routePathConfig.grantConsent : routePathConfig.landing,
        search: location.search,
      });
    },
    [authorizeUser, csrfCode, dispatch, navigate, location.search, refreshCsrfCode],
  );

  return {
    login,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
  };
};
